import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik'; // Added FormikHelpers
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Button from '../../../components/Button/Button';
import Map, { ICoordinate, IMapMethods } from '../../../components/Map/Map';
import TextArea from '../../../components/TextArea/TextArea';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import { modalStyle } from '../../style/modal';
import Layout from '../../components/Layout/Layout';
import { EProjectType, unit as unitEnum } from '../../enum/project';
import { IProjectInfoForm, ICountry } from './IProjectInfo';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import * as ProjectApiService from '../../services/ProjectApiService';
import Dialog from '../../../components/Dialog/Dialog';
import { debounce } from 'lodash';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Grid, MenuItem, Modal, Select, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { IDynamicProjectGetData, IProject } from '../../api/IApiResponse';
import * as ProjectTypeService from '../../services/ProjectTypeService';
import { cleanResults } from '../../Dynamic/store/project/resultsSlice';

import { setProject, cleanProject, updateProjectDetails, selectDynamicProject, saveProjectDraft } from '../../Dynamic/store/project/projectslice';
import { logDebug, logError, logWarn, LogTag } from '../../utils/utils';

const ProjectInfo: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    //const [searchParams] = useSearchParams();
    const { id: projectIdFromUrl } = useParams<{ id: string }>(); // Leggi l'ID dall'URL

    const dynamicProject = useAppSelector(selectDynamicProject);

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [countries, setCountries] = useState<ICountry[]>([]);
    const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [mapImage, setMapImage] = useState<string>('');
    const [ErrorDialog, setErrorDialog] = useState<string>('');
    const [disableNext, setDisableNext] = useState<boolean>(false);
    const [isExistingProject, setIsExistingProject] = useState<boolean>(!!projectIdFromUrl); // Determina da URL

    const mapRef = useRef<IMapMethods>(null);

    const closeErrorDialog = () => setErrorDialog('');

    // Questa funzione ora prepara solo i dati, non fa il dispatch
    const prepareProjectDataForDispatch = (values: IProjectInfoForm): Partial<IProject> => {
        // Ensure coordinates are always numbers, not undefined
        const safeCoordinates: [number, number] = [values.coordinates[0] !== undefined ? values.coordinates[0] : 44.5215, values.coordinates[1] !== undefined ? values.coordinates[1] : 11.2833];

        return {
            // guid: projectIdFromUrl || undefined, // Il GUID per i nuovi progetti sarà gestito dal thunk
            projectName: values.projectName,
            // nrProject: values.nrProject, // Remove this line - moving to projectInfo.data
            mapImage: mapImage || values.mapImage,
            unit: values.unit === unitEnum.SI || values.unit === unitEnum.Imperial ? values.unit : unitEnum.SI,
            type: isExistingProject && dynamicProject.data?.type ? dynamicProject.data.type : EProjectType.geogrid,
            ...(isExistingProject && dynamicProject.data // Per progetti esistenti, includi il GUID e altri dati
                ? {
                      guid: projectIdFromUrl, // Assicurati che il GUID sia qui per gli aggiornamenti
                      method: dynamicProject.data.method,
                      projectInfo: {
                          ...dynamicProject.data.projectInfo,
                          coordinates: safeCoordinates, // Move coordinates here
                          projectOwner: values.owner, // Move owner to projectInfo.projectOwner
                          owner: values.owner, // Also set owner for compatibility
                          designerFirmName: values.designerFirmName, // designerFirmName is already in projectInfo
                          designerFirmCountry: values.designerFirmCountry, // Move designerFirmCountry here
                          productCountry: values.productCountry || undefined, // productCountry is already in projectInfo
                          data: {
                              ...dynamicProject.data.projectInfo?.data,
                              notes: values.notes, // notes goes in projectInfo.data
                              designerFirmCity: values.designerFirmCity, // Move designerFirmCity here
                              nrProject: values.nrProject, // Move nrProject here
                          },
                      },
                      readOnly: dynamicProject.data.readOnly,
                      hasBeenRan: dynamicProject.data.hasBeenRan,
                      idApp: dynamicProject.data.idApp,
                      idUser: dynamicProject.data.idUser,
                      creationDate: dynamicProject.data.creationDate,
                      country: dynamicProject.data.country,
                      language: dynamicProject.data.language,
                      status: dynamicProject.data.status,
                      isCalculated: dynamicProject.data.isCalculated,
                      isValid: dynamicProject.data.isValid,
                      isApproved: dynamicProject.data.isApproved,
                      lastSeen: dynamicProject.data.lastSeen,
                      sharedUsers: dynamicProject.data.sharedUsers,
                      geogridHasBeenRan: dynamicProject.data.geogridHasBeenRan,
                      disableRun: dynamicProject.data.disableRun,
                  }
                : {
                      projectInfo: {
                          coordinates: safeCoordinates, // Move coordinates here for new projects
                          projectOwner: values.owner, // Move owner to projectInfo.projectOwner for new projects
                          owner: values.owner, // Also set owner for compatibility
                          designerFirmName: values.designerFirmName, // designerFirmName is already in projectInfo
                          designerFirmCountry: values.designerFirmCountry, // Move designerFirmCountry here for new projects
                          productCountry: values.productCountry || undefined, // productCountry is already in projectInfo
                          products: [],
                          data: {
                              notes: values.notes, // notes goes in projectInfo.data for new projects too
                              designerFirmCity: values.designerFirmCity, // Move designerFirmCity here for new projects
                              nrProject: values.nrProject, // Move nrProject here for new projects
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          } as any,
                      },
                  }), // Per i nuovi progetti, il thunk gestirà il GUID
        };
    };

    useEffect(() => {
        logDebug('ProjectInfo', 'useEffect triggered. Project ID from URL:', projectIdFromUrl, LogTag.PROJECT_INFO);
        logDebug('ProjectInfo', 'Current Redux project data:', dynamicProject.data, LogTag.PROJECT_INFO);

        const fetchProjectAndUpdateState = async (id: string) => {
            setLoadingProject(true);
            logDebug('ProjectInfo', 'Fetching existing project. ID:', id, LogTag.API_CALLS);
            try {
                // Use new API: GET /api/Project/projects/{guid}
                const loadResult = await ProjectApiService.loadProjectByGuid(id);

                if (!loadResult.success || !loadResult.data) {
                    throw new Error(`Project ${id} not found via API: ${loadResult.error}`);
                }

                logDebug('ProjectInfo', 'Project loaded successfully from backend', loadResult.data, LogTag.API_CALLS);

                // Transform backend project to frontend format
                const transformedProjectData = ProjectApiService.transformProjectFromBackend(loadResult.data);

                logDebug('ProjectInfo', 'Transformed project data:', transformedProjectData, LogTag.DATA_TRANSFORM);

                dispatch(setProject(transformedProjectData));
                setIsExistingProject(true);
                if (transformedProjectData.mapImage) {
                    setMapImage(transformedProjectData.mapImage);
                }
            } catch (error) {
                logError('ProjectInfo', 'Error fetching project:', error, LogTag.ERROR_HANDLING);
                setErrorDialog(t('errors.projectLoad.message') || 'Error loading project.');
                dispatch(cleanProject());
                setIsExistingProject(false);
                setMapImage('');
            } finally {
                setLoadingProject(false);
            }
        };

        if (projectIdFromUrl) {
            // Se c'è un ID nell'URL
            setIsExistingProject(true);
            if (dynamicProject.data?.guid === projectIdFromUrl) {
                // I dati in Redux corrispondono all'URL, usali
                logDebug('ProjectInfo', 'Using Redux data as it matches URL ID:', projectIdFromUrl, LogTag.PROJECT_INFO);
                setMapImage(dynamicProject.data.mapImage || '');
                setLoadingProject(false);
            } else {
                // I dati in Redux non corrispondono o mancano, carica dall'API
                logDebug('ProjectInfo', 'Redux data mismatch or missing for URL ID. Fetching project:', projectIdFromUrl, LogTag.API_CALLS);
                fetchProjectAndUpdateState(projectIdFromUrl);
            }
        } else {
            // Nessun ID nell'URL -> Nuovo progetto
            logDebug('ProjectInfo', 'No project ID in URL. Initializing for a new project.', undefined, LogTag.PROJECT_SETUP);
            // Se c'è qualcosa in Redux che non ha guid o è marcato come newProject, puliscilo.
            // Altrimenti, se l'utente arriva qui esplicitamente senza ID, è un nuovo progetto.
            if (!dynamicProject.data?.newProject && dynamicProject.data?.guid) {
                // Potrebbe essere uno stato Redux "sporco", pulisci per un nuovo progetto
                dispatch(cleanProject());
            } else if (dynamicProject.data?.newProject && dynamicProject.data?.guid) {
                // È un nuovo progetto già parzialmente inizializzato in Redux (es. da un draft precedente non finalizzato)
                // Non pulire, usa i dati draft.
                logDebug('ProjectInfo', 'Continuing with new project draft from Redux.', undefined, LogTag.PROJECT_INFO);
            } else {
                // Pulisci per assicurare uno stato vergine per un nuovo progetto
                dispatch(cleanProject());
                dispatch(cleanResults());
            }
            setIsExistingProject(false);
            setMapImage('');
            setLoadingProject(false);
        }
        // This effect is route-driven: reacts to URL changes and guid mismatches.
        // Using guid primitive (not the full data object) prevents re-triggering when dispatch(setProject) updates data.
    }, [dispatch, projectIdFromUrl, t, dynamicProject.data?.guid]);

    useEffect(() => {
        // Reason: Complete country list matching MACRO application (https://macro.maccaferri.com/project-info)
        const fetchedCountries: ICountry[] = [
            { alpha2Code: 'AF', name: 'Afghanistan' },
            { alpha2Code: 'AX', name: 'Åland Islands' },
            { alpha2Code: 'AL', name: 'Albania' },
            { alpha2Code: 'DZ', name: 'Algeria' },
            { alpha2Code: 'AS', name: 'American Samoa' },
            { alpha2Code: 'AD', name: 'Andorra' },
            { alpha2Code: 'AO', name: 'Angola' },
            { alpha2Code: 'AI', name: 'Anguilla' },
            { alpha2Code: 'AQ', name: 'Antarctica' },
            { alpha2Code: 'AG', name: 'Antigua and Barbuda' },
            { alpha2Code: 'AR', name: 'Argentina' },
            { alpha2Code: 'AM', name: 'Armenia' },
            { alpha2Code: 'AW', name: 'Aruba' },
            { alpha2Code: 'AU', name: 'Australia' },
            { alpha2Code: 'AT', name: 'Austria' },
            { alpha2Code: 'AZ', name: 'Azerbaijan' },
            { alpha2Code: 'BS', name: 'Bahamas' },
            { alpha2Code: 'BH', name: 'Bahrain' },
            { alpha2Code: 'BD', name: 'Bangladesh' },
            { alpha2Code: 'BB', name: 'Barbados' },
            { alpha2Code: 'BY', name: 'Belarus' },
            { alpha2Code: 'BE', name: 'Belgium' },
            { alpha2Code: 'BZ', name: 'Belize' },
            { alpha2Code: 'BJ', name: 'Benin' },
            { alpha2Code: 'BM', name: 'Bermuda' },
            { alpha2Code: 'BT', name: 'Bhutan' },
            { alpha2Code: 'BO', name: 'Bolivia' },
            { alpha2Code: 'BA', name: 'Bosnia and Herzegovina' },
            { alpha2Code: 'BW', name: 'Botswana' },
            { alpha2Code: 'BV', name: 'Bouvet Island' },
            { alpha2Code: 'BR', name: 'Brazil' },
            { alpha2Code: 'IO', name: 'British Indian Ocean Territory' },
            { alpha2Code: 'VG', name: 'British Virgin Islands' },
            { alpha2Code: 'BN', name: 'Brunei' },
            { alpha2Code: 'BG', name: 'Bulgaria' },
            { alpha2Code: 'BF', name: 'Burkina Faso' },
            { alpha2Code: 'BI', name: 'Burundi' },
            { alpha2Code: 'KH', name: 'Cambodia' },
            { alpha2Code: 'CM', name: 'Cameroon' },
            { alpha2Code: 'CA', name: 'Canada' },
            { alpha2Code: 'CV', name: 'Cape Verde' },
            { alpha2Code: 'BQ', name: 'Caribbean Netherlands' },
            { alpha2Code: 'KY', name: 'Cayman Islands' },
            { alpha2Code: 'CF', name: 'Central African Republic' },
            { alpha2Code: 'TD', name: 'Chad' },
            { alpha2Code: 'CL', name: 'Chile' },
            { alpha2Code: 'CN', name: 'China' },
            { alpha2Code: 'CX', name: 'Christmas Island' },
            { alpha2Code: 'CC', name: 'Cocos (Keeling) Islands' },
            { alpha2Code: 'CO', name: 'Colombia' },
            { alpha2Code: 'KM', name: 'Comoros' },
            { alpha2Code: 'CD', name: 'DR Congo' },
            { alpha2Code: 'CK', name: 'Cook Islands' },
            { alpha2Code: 'CR', name: 'Costa Rica' },
            { alpha2Code: 'HR', name: 'Croatia' },
            { alpha2Code: 'CU', name: 'Cuba' },
            { alpha2Code: 'CW', name: 'Curaçao' },
            { alpha2Code: 'CY', name: 'Cyprus' },
            { alpha2Code: 'CZ', name: 'Czechia' },
            { alpha2Code: 'DK', name: 'Denmark' },
            { alpha2Code: 'DJ', name: 'Djibouti' },
            { alpha2Code: 'DM', name: 'Dominica' },
            { alpha2Code: 'DO', name: 'Dominican Republic' },
            { alpha2Code: 'EC', name: 'Ecuador' },
            { alpha2Code: 'EG', name: 'Egypt' },
            { alpha2Code: 'SV', name: 'El Salvador' },
            { alpha2Code: 'GQ', name: 'Equatorial Guinea' },
            { alpha2Code: 'ER', name: 'Eritrea' },
            { alpha2Code: 'EE', name: 'Estonia' },
            { alpha2Code: 'ET', name: 'Ethiopia' },
            { alpha2Code: 'FK', name: 'Falkland Islands' },
            { alpha2Code: 'FO', name: 'Faroe Islands' },
            { alpha2Code: 'FJ', name: 'Fiji' },
            { alpha2Code: 'FI', name: 'Finland' },
            { alpha2Code: 'FR', name: 'France' },
            { alpha2Code: 'GF', name: 'French Guiana' },
            { alpha2Code: 'PF', name: 'French Polynesia' },
            { alpha2Code: 'TF', name: 'French Southern and Antarctic Lands' },
            { alpha2Code: 'GA', name: 'Gabon' },
            { alpha2Code: 'GM', name: 'Gambia' },
            { alpha2Code: 'GE', name: 'Georgia' },
            { alpha2Code: 'DE', name: 'Germany' },
            { alpha2Code: 'GH', name: 'Ghana' },
            { alpha2Code: 'GI', name: 'Gibraltar' },
            { alpha2Code: 'GR', name: 'Greece' },
            { alpha2Code: 'GL', name: 'Greenland' },
            { alpha2Code: 'GD', name: 'Grenada' },
            { alpha2Code: 'GP', name: 'Guadeloupe' },
            { alpha2Code: 'GU', name: 'Guam' },
            { alpha2Code: 'GT', name: 'Guatemala' },
            { alpha2Code: 'GG', name: 'Guernsey' },
            { alpha2Code: 'GW', name: 'Guinea-Bissau' },
            { alpha2Code: 'GN', name: 'Guinea' },
            { alpha2Code: 'GY', name: 'Guyana' },
            { alpha2Code: 'HT', name: 'Haiti' },
            { alpha2Code: 'HM', name: 'Heard Island and McDonald Islands' },
            { alpha2Code: 'HN', name: 'Honduras' },
            { alpha2Code: 'HK', name: 'Hong Kong' },
            { alpha2Code: 'HU', name: 'Hungary' },
            { alpha2Code: 'IS', name: 'Iceland' },
            { alpha2Code: 'IN', name: 'India' },
            { alpha2Code: 'ID', name: 'Indonesia' },
            { alpha2Code: 'IR', name: 'Iran' },
            { alpha2Code: 'IQ', name: 'Iraq' },
            { alpha2Code: 'IE', name: 'Ireland' },
            { alpha2Code: 'IM', name: 'Isle of Man' },
            { alpha2Code: 'IL', name: 'Israel' },
            { alpha2Code: 'IT', name: 'Italy' },
            { alpha2Code: 'CI', name: 'Ivory Coast' },
            { alpha2Code: 'JM', name: 'Jamaica' },
            { alpha2Code: 'JP', name: 'Japan' },
            { alpha2Code: 'JE', name: 'Jersey' },
            { alpha2Code: 'JO', name: 'Jordan' },
            { alpha2Code: 'KZ', name: 'Kazakhstan' },
            { alpha2Code: 'KE', name: 'Kenya' },
            { alpha2Code: 'KI', name: 'Kiribati' },
            { alpha2Code: 'KW', name: 'Kuwait' },
            { alpha2Code: 'KG', name: 'Kyrgyzstan' },
            { alpha2Code: 'LA', name: 'Laos' },
            { alpha2Code: 'LV', name: 'Latvia' },
            { alpha2Code: 'LB', name: 'Lebanon' },
            { alpha2Code: 'LS', name: 'Lesotho' },
            { alpha2Code: 'LR', name: 'Liberia' },
            { alpha2Code: 'LY', name: 'Libya' },
            { alpha2Code: 'LI', name: 'Liechtenstein' },
            { alpha2Code: 'LT', name: 'Lithuania' },
            { alpha2Code: 'LU', name: 'Luxembourg' },
            { alpha2Code: 'MO', name: 'Macau' },
            { alpha2Code: 'MG', name: 'Madagascar' },
            { alpha2Code: 'MW', name: 'Malawi' },
            { alpha2Code: 'MY', name: 'Malaysia' },
            { alpha2Code: 'MV', name: 'Maldives' },
            { alpha2Code: 'ML', name: 'Mali' },
            { alpha2Code: 'MT', name: 'Malta' },
            { alpha2Code: 'MH', name: 'Marshall Islands' },
            { alpha2Code: 'MQ', name: 'Martinique' },
            { alpha2Code: 'MR', name: 'Mauritania' },
            { alpha2Code: 'MU', name: 'Mauritius' },
            { alpha2Code: 'YT', name: 'Mayotte' },
            { alpha2Code: 'MX', name: 'Mexico' },
            { alpha2Code: 'FM', name: 'Micronesia' },
            { alpha2Code: 'MD', name: 'Moldova' },
            { alpha2Code: 'MC', name: 'Monaco' },
            { alpha2Code: 'MN', name: 'Mongolia' },
            { alpha2Code: 'ME', name: 'Montenegro' },
            { alpha2Code: 'MS', name: 'Montserrat' },
            { alpha2Code: 'MA', name: 'Morocco' },
            { alpha2Code: 'MZ', name: 'Mozambique' },
            { alpha2Code: 'MM', name: 'Myanmar' },
            { alpha2Code: 'NA', name: 'Namibia' },
            { alpha2Code: 'NR', name: 'Nauru' },
            { alpha2Code: 'NP', name: 'Nepal' },
            { alpha2Code: 'NL', name: 'Netherlands' },
            { alpha2Code: 'NC', name: 'New Caledonia' },
            { alpha2Code: 'NZ', name: 'New Zealand' },
            { alpha2Code: 'NI', name: 'Nicaragua' },
            { alpha2Code: 'NG', name: 'Nigeria' },
            { alpha2Code: 'NE', name: 'Niger' },
            { alpha2Code: 'NU', name: 'Niue' },
            { alpha2Code: 'NF', name: 'Norfolk Island' },
            { alpha2Code: 'MP', name: 'Northern Mariana Islands' },
            { alpha2Code: 'KP', name: 'North Korea' },
            { alpha2Code: 'MK', name: 'North Macedonia' },
            { alpha2Code: 'NO', name: 'Norway' },
            { alpha2Code: 'OM', name: 'Oman' },
            { alpha2Code: 'PK', name: 'Pakistan' },
            { alpha2Code: 'PW', name: 'Palau' },
            { alpha2Code: 'PS', name: 'Palestine' },
            { alpha2Code: 'PA', name: 'Panama' },
            { alpha2Code: 'PG', name: 'Papua New Guinea' },
            { alpha2Code: 'PY', name: 'Paraguay' },
            { alpha2Code: 'PE', name: 'Peru' },
            { alpha2Code: 'PH', name: 'Philippines' },
            { alpha2Code: 'PN', name: 'Pitcairn Islands' },
            { alpha2Code: 'PL', name: 'Poland' },
            { alpha2Code: 'PT', name: 'Portugal' },
            { alpha2Code: 'PR', name: 'Puerto Rico' },
            { alpha2Code: 'QA', name: 'Qatar' },
            { alpha2Code: 'CG', name: 'Republic of the Congo' },
            { alpha2Code: 'RE', name: 'Réunion' },
            { alpha2Code: 'RO', name: 'Romania' },
            { alpha2Code: 'RU', name: 'Russia' },
            { alpha2Code: 'RW', name: 'Rwanda' },
            { alpha2Code: 'BL', name: 'Saint Barthélemy' },
            { alpha2Code: 'SH', name: 'Saint Helena, Ascension and Tristan da Cunha' },
            { alpha2Code: 'KN', name: 'Saint Kitts and Nevis' },
            { alpha2Code: 'LC', name: 'Saint Lucia' },
            { alpha2Code: 'MF', name: 'Saint Martin' },
            { alpha2Code: 'PM', name: 'Saint Pierre and Miquelon' },
            { alpha2Code: 'VC', name: 'Saint Vincent and the Grenadines' },
            { alpha2Code: 'WS', name: 'Samoa' },
            { alpha2Code: 'SM', name: 'San Marino' },
            { alpha2Code: 'ST', name: 'São Tomé and Príncipe' },
            { alpha2Code: 'SA', name: 'Saudi Arabia' },
            { alpha2Code: 'SN', name: 'Senegal' },
            { alpha2Code: 'RS', name: 'Serbia' },
            { alpha2Code: 'SC', name: 'Seychelles' },
            { alpha2Code: 'SL', name: 'Sierra Leone' },
            { alpha2Code: 'SG', name: 'Singapore' },
            { alpha2Code: 'SX', name: 'Sint Maarten' },
            { alpha2Code: 'SK', name: 'Slovakia' },
            { alpha2Code: 'SI', name: 'Slovenia' },
            { alpha2Code: 'SB', name: 'Solomon Islands' },
            { alpha2Code: 'SO', name: 'Somalia' },
            { alpha2Code: 'ZA', name: 'South Africa' },
            { alpha2Code: 'GS', name: 'South Georgia' },
            { alpha2Code: 'KR', name: 'South Korea' },
            { alpha2Code: 'SS', name: 'South Sudan' },
            { alpha2Code: 'ES', name: 'Spain' },
            { alpha2Code: 'LK', name: 'Sri Lanka' },
            { alpha2Code: 'SD', name: 'Sudan' },
            { alpha2Code: 'SR', name: 'Suriname' },
            { alpha2Code: 'SJ', name: 'Svalbard and Jan Mayen' },
            { alpha2Code: 'SZ', name: 'Eswatini' },
            { alpha2Code: 'SE', name: 'Sweden' },
            { alpha2Code: 'CH', name: 'Switzerland' },
            { alpha2Code: 'SY', name: 'Syria' },
            { alpha2Code: 'TW', name: 'Taiwan' },
            { alpha2Code: 'TJ', name: 'Tajikistan' },
            { alpha2Code: 'TZ', name: 'Tanzania' },
            { alpha2Code: 'TH', name: 'Thailand' },
            { alpha2Code: 'TL', name: 'Timor-Leste' },
            { alpha2Code: 'TG', name: 'Togo' },
            { alpha2Code: 'TK', name: 'Tokelau' },
            { alpha2Code: 'TO', name: 'Tonga' },
            { alpha2Code: 'TT', name: 'Trinidad and Tobago' },
            { alpha2Code: 'TN', name: 'Tunisia' },
            { alpha2Code: 'TR', name: 'Turkey' },
            { alpha2Code: 'TM', name: 'Turkmenistan' },
            { alpha2Code: 'TC', name: 'Turks and Caicos Islands' },
            { alpha2Code: 'TV', name: 'Tuvalu' },
            { alpha2Code: 'UG', name: 'Uganda' },
            { alpha2Code: 'UA', name: 'Ukraine' },
            { alpha2Code: 'AE', name: 'United Arab Emirates' },
            { alpha2Code: 'GB', name: 'United Kingdom' },
            { alpha2Code: 'US', name: 'United States' },
            { alpha2Code: 'UM', name: 'United States Minor Outlying Islands' },
            { alpha2Code: 'VI', name: 'United States Virgin Islands' },
            { alpha2Code: 'UY', name: 'Uruguay' },
            { alpha2Code: 'UZ', name: 'Uzbekistan' },
            { alpha2Code: 'VU', name: 'Vanuatu' },
            { alpha2Code: 'VA', name: 'Vatican City' },
            { alpha2Code: 'VE', name: 'Venezuela' },
            { alpha2Code: 'VN', name: 'Vietnam' },
            { alpha2Code: 'WF', name: 'Wallis and Futuna' },
            { alpha2Code: 'EH', name: 'Western Sahara' },
            { alpha2Code: 'YE', name: 'Yemen' },
            { alpha2Code: 'ZM', name: 'Zambia' },
            { alpha2Code: 'ZW', name: 'Zimbabwe' },
        ];
        setCountries(fetchedCountries);
        setLoadingCountries(false);
    }, []);

    /**
     * Validates coordinate values to ensure they are within valid ranges
     * @param lat - Latitude value
     * @param lng - Longitude value
     * @returns true if coordinates are valid, false otherwise
     */
    const validateCoordinates = (lat: number, lng: number): boolean => {
        return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    };

    const getProductCountry = async (coordinates: ICoordinate, formikSetValues: (values: React.SetStateAction<IProjectInfoForm>, shouldValidate?: boolean) => void) => {
        setDisableNext(true);
        try {
            if (coordinates && coordinates.latitude != null && coordinates.longitude != null) {
                // Validate coordinate ranges
                if (!validateCoordinates(coordinates.latitude, coordinates.longitude)) {
                    logWarn('ProjectInfo', 'Invalid coordinate ranges:', coordinates, LogTag.VALIDATION);
                    formikSetValues(currentValues => ({ ...currentValues, productCountry: null }), true);
                    setErrorDialog('coordinates');
                    return;
                }

                const response = await BeMaccaferriStoreHelper.getCountryByCoordinatesAsync(coordinates.latitude, coordinates.longitude);
                const productCountryCode = response?.extratags?.['ISO3166-1:alpha2'];
                if (!productCountryCode) {
                    logWarn('ProjectInfo', 'Country code not found in response:', response, LogTag.API_CALLS);
                    formikSetValues(currentValues => ({ ...currentValues, productCountry: null }), true);
                    setErrorDialog('coordinates');
                } else {
                    formikSetValues(currentValues => ({ ...currentValues, productCountry: productCountryCode }), true);
                }
            } else {
                formikSetValues(currentValues => ({ ...currentValues, productCountry: null }), true);
                setErrorDialog('coordinates');
            }
        } catch (ex) {
            logError('ProjectInfo', 'Error getting product country:', ex, LogTag.ERROR_HANDLING);
            formikSetValues(currentValues => ({ ...currentValues, productCountry: null }), true);
            setErrorDialog('coordinates');
        } finally {
            setDisableNext(false);
        }
    };
    const debounceGetProductCountry = debounce(getProductCountry, 1200);

    const closeMapModal = () => setOpenModal(false);
    const handleMapImageUpdate = (mapImageUrl: string) => {
        setMapImage(mapImageUrl);
    };

    const handleInitialValues = (): IProjectInfoForm => {
        logDebug('ProjectInfo', 'Initializing form values', undefined, LogTag.FORM_SETUP);
        logDebug('ProjectInfo', 'Existing project:', isExistingProject, LogTag.FORM_SETUP);
        logDebug('ProjectInfo', 'Dynamic project data:', dynamicProject.data, LogTag.FORM_SETUP);

        const data = dynamicProject.data;
        // mapImage is set in the route-loading useEffect (lines 140-142, 160), not here.
        // Calling setState during render (Formik initialValues) violates React rules.

        return {
            projectId: data?.guid ?? '',
            projectName: data?.projectName ?? '',
            nrProject: data?.projectInfo?.data?.nrProject ?? '', // Access nrProject from projectInfo.data
            owner: data?.projectInfo?.projectOwner ?? '', // Access from projectInfo.projectOwner
            projectType: data?.type && data.type !== EProjectType.geogrid ? data.type : EProjectType.geogrid,
            notes: data?.projectInfo?.data?.notes ?? '', // Read notes from projectInfo.data.notes
            designerFirmName: data?.projectInfo?.designerFirmName ?? '',
            designerFirmCountry: data?.projectInfo?.designerFirmCountry ?? '', // Access from projectInfo
            designerFirmCity: data?.projectInfo?.data?.designerFirmCity ?? '', // Access from projectInfo.data
            coordinates: data?.projectInfo?.coordinates ?? [44.5215, 11.2833], // Access coordinates from projectInfo
            productCountry: data?.projectInfo?.productCountry ?? null, // Access productCountry from projectInfo
            mapImage: data?.mapImage ?? '', // Formik uses this, but onSubmit prioritizes local `mapImage`
            unit: data?.unit ?? unitEnum.SI,
        };
    };

    const requiredMSG = t('project_info_formik_required');
    const positionNotValidMSG = t('errors.projectCoordinatesCountry.field');
    const ProjectSchema = Yup.object().shape({
        projectName: Yup.string().required(requiredMSG),
        owner: Yup.string().required(requiredMSG),
        nrProject: Yup.string().nullable(),
        designerFirmName: Yup.string().required(requiredMSG),
        designerFirmCountry: Yup.string().required(requiredMSG),
        designerFirmCity: Yup.string().required(requiredMSG),
        notes: Yup.string().nullable(),
        coordinates: Yup.array()
            .of(Yup.number().typeError(t('project_info_formik_coord_must_be_number') || 'Coordinate must be a number'))
            .min(2, t('project_info_formik_coord_required') || 'Coordinates are required')
            .max(2)
            .test('is-valid-coordinates', requiredMSG, value => {
                if (!Array.isArray(value) || value.length !== 2 || value[0] == null || value[1] == null) {
                    return false;
                }
                const [lat, lng] = value;
                return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            }),
        productCountry: Yup.string().required(positionNotValidMSG).nullable(),
    });

    const handleFormSubmit = async (values: IProjectInfoForm, { setSubmitting }: FormikHelpers<IProjectInfoForm>) => {
        setSubmitting(true);
        const projectDataToSave = prepareProjectDataForDispatch(values);

        if (!projectIdFromUrl) {
            // È un nuovo progetto
            logDebug('ProjectInfo', 'Attempting to save new project draft:', projectDataToSave, LogTag.PROJECT_SETUP);
            try {
                // Aspetta che il thunk completi e ritorni il GUID
                const resultAction = await dispatch(saveProjectDraft(projectDataToSave));

                if (saveProjectDraft.fulfilled.match(resultAction)) {
                    const guidForNav = resultAction.payload; // Questo è il GUID ritornato dal thunk
                    logDebug('ProjectInfo', 'New project draft saved. Navigating with GUID:', guidForNav, LogTag.PROJECT_SETUP);
                    navigate(`/project-type/${guidForNav}`);
                } else {
                    // Gestisci il caso in cui il thunk è rejected
                    logError('ProjectInfo', 'Failed to save project draft:', resultAction.error, LogTag.ERROR_HANDLING);
                    setErrorDialog(t('errors.projectSave.message', 'Failed to save project draft.') || 'Failed to save project draft.');
                }
            } catch (error) {
                logError('ProjectInfo', 'Error dispatching saveProjectDraft:', error, LogTag.ERROR_HANDLING);
                setErrorDialog(t('errors.unexpected.message', 'An unexpected error occurred.') || 'An unexpected error occurred.');
            } finally {
                setSubmitting(false);
            }
        } else {
            // È un progetto esistente
            logDebug('ProjectInfo', `Updating existing project details for GUID: ${projectIdFromUrl}`, projectDataToSave, LogTag.PROJECT_INFO);
            // Per i progetti esistenti, puoi usare updateProjectDetails
            // Assicurati che projectDataToSave includa il guid per updateProjectDetails
            dispatch(updateProjectDetails({ ...projectDataToSave, guid: projectIdFromUrl } as IProject));

            // La navigazione per progetti esistenti può avvenire subito dopo il dispatch sincrono
            // o puoi attendere se updateProjectDetails diventasse un thunk.
            // Per ora, assumiamo che l'aggiornamento di Redux sia sufficientemente veloce.
            const guidForNav = projectIdFromUrl; // Usiamo l'ID dall'URL che è quello del progetto esistente
            if (dynamicProject.data?.type === EProjectType.geogrid) {
                // For geogrid projects, navigate directly to the geogrid page
                navigate(`/geogrid/${guidForNav}`);
            } else {
                navigate(`/designCondition/${guidForNav}`);
            }
            setSubmitting(false);
        }
    };

    const submitButtonText = isExistingProject ? t('project_info_save_and_continue') : t('next_button');

    return (
        <Layout>
            <Dialog
                open={!!ErrorDialog && ErrorDialog.length > 0}
                title={`${t(`errors.${ErrorDialog === 'coordinates' ? 'projectCoordinatesCountry' : 'projectPut'}.title`)}`}
                onClose={closeErrorDialog}
                actions={[{ text: t('ok') || 'OK', callback: closeErrorDialog, color: 'primary' }]}>
                <div>{`${t(`errors.${ErrorDialog === 'coordinates' ? 'projectCoordinatesCountry' : 'projectPut'}.message`)}`}</div>
            </Dialog>

            <Grid container style={{ paddingBottom: '80px', alignItems: 'center' }} spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h5" component="h1" align="center" gutterBottom className="titNewProject">
                        {t('project_info_title')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    {loadingCountries || loadingProject ? (
                        <Box display="flex" justifyContent="center" p={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Formik initialValues={handleInitialValues()} validationSchema={ProjectSchema} enableReinitialize={true} onSubmit={handleFormSubmit}>
                            {({ values, setValues, handleChange, isSubmitting, submitForm, setFieldValue }) => {
                                /**
                                 * Common handler for coordinate changes with validation
                                 * @param value - Input value
                                 * @param index - 0 for latitude, 1 for longitude
                                 * @param fieldName - Field name for setting value
                                 */
                                const handleCoordinateChange = (value: string, index: 0 | 1, fieldName: string) => {
                                    const numValue = parseFloat(value);
                                    setFieldValue(fieldName, isNaN(numValue) ? value : numValue);

                                    const currentCoords = values.coordinates || [null, null];
                                    const otherIndex = index === 0 ? 1 : 0;
                                    const otherValue = currentCoords[otherIndex];

                                    if (otherValue != null && !isNaN(numValue)) {
                                        const newCoords = index === 0 ? { latitude: numValue, longitude: otherValue } : { latitude: otherValue, longitude: numValue };

                                        if (validateCoordinates(newCoords.latitude, newCoords.longitude)) {
                                            setDisableNext(true);
                                            debounceGetProductCountry(newCoords, setValues);
                                        } else {
                                            logWarn('ProjectInfo', 'Invalid coordinate range entered:', newCoords, LogTag.VALIDATION);
                                            setValues(prev => ({ ...prev, productCountry: null }), true);
                                        }
                                    } else if (isNaN(numValue)) {
                                        setValues(prev => ({ ...prev, productCountry: null }), true);
                                    }
                                };

                                const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleCoordinateChange(e.target.value, 0, 'coordinates[0]');
                                };

                                const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleCoordinateChange(e.target.value, 1, 'coordinates[1]');
                                };

                                return (
                                    <Form className="d-flex align-items-center h-100 project-info-form overflow-auto form-nuovoProgetto">
                                        <div className="d-flex justify-content-between w-100 full-height">
                                            <Grid container item spacing={3} style={{ alignItems: 'flex-start' }} p={1}>
                                                {/* Colonna Sinistra */}
                                                <Grid item xs={12} md={6}>
                                                    <Box className="d-flex flex-column gap-2 form-section-box">
                                                        <Typography variant="h6" className="formSectionTitle">
                                                            {t('project_info_project_detail_title')}
                                                        </Typography>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_project_detail_project_name')}</label>
                                                            <Field type="text" name="projectName" placeholder={t('placeholder') as string} className="form-field" />
                                                            <ErrorMessage name="projectName" component="span" className="error-color form-error" />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_project_detail_project_number')}</label>
                                                            <Field type="text" name="nrProject" placeholder={t('placeholder') as string} className="form-field" />
                                                            <ErrorMessage name="nrProject" component="span" className="error-color form-error" />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_project_detail_owner')}</label>
                                                            <Field type="text" name="owner" placeholder={t('placeholder') as string} className="form-field" />
                                                            <ErrorMessage name="owner" component="span" className="error-color form-error" />
                                                        </div>
                                                    </Box>
                                                    <Box className="d-flex flex-column gap-2 form-section-box" mt={4}>
                                                        <Typography variant="h6" className="formSectionTitle">
                                                            {t('project_info_designer_detail_title')}
                                                        </Typography>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_designer_detail_firm')}</label>
                                                            <Field type="text" name="designerFirmName" placeholder={t('placeholder') as string} className="form-field" />
                                                            <ErrorMessage name="designerFirmName" component="span" className="error-color form-error" />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_designer_detail_country')}</label>
                                                            <Select
                                                                fullWidth
                                                                name="designerFirmCountry"
                                                                size="small"
                                                                value={values.designerFirmCountry || ''}
                                                                onChange={handleChange}
                                                                displayEmpty
                                                                className="form-field">
                                                                <MenuItem value="" disabled>
                                                                    {t('select_country')}
                                                                </MenuItem>
                                                                {countries.map((country: ICountry) => (
                                                                    <MenuItem key={country.alpha2Code} value={country.alpha2Code}>
                                                                        {country.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            <ErrorMessage name="designerFirmCountry" component="span" className="error-color form-error" />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <label className="formLabel">{t('project_info_designer_detail_city')}</label>
                                                            <Field type="text" name="designerFirmCity" placeholder={t('placeholder') as string} className="form-field" />
                                                            <ErrorMessage name="designerFirmCity" component="span" className="error-color form-error" />
                                                        </div>
                                                    </Box>
                                                </Grid>
                                                {/* Colonna Destra */}
                                                <Grid item xs={12} md={6}>
                                                    <Box className="d-flex flex-column gap-2 full-height form-section-box">
                                                        <Typography variant="h6" className="formSectionTitle">
                                                            {t('project_info_location')}
                                                        </Typography>
                                                        <Grid container item spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <div className="d-flex flex-column">
                                                                    <label className="formLabel">{t('project_info_project_detail_location_latitude')}</label>
                                                                    <Field
                                                                        type="number"
                                                                        step="any"
                                                                        name="coordinates[0]"
                                                                        placeholder={t('placeholder') as string}
                                                                        value={values.coordinates?.[0] ?? ''}
                                                                        onChange={handleLatChange}
                                                                        className="form-field"
                                                                    />
                                                                </div>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <div className="d-flex flex-column">
                                                                    <label className="formLabel">{t('project_info_project_detail_location_longitude')}</label>
                                                                    <Field
                                                                        type="number"
                                                                        step="any"
                                                                        name="coordinates[1]"
                                                                        placeholder={t('placeholder') as string}
                                                                        value={values.coordinates?.[1] ?? ''}
                                                                        onChange={handleLngChange}
                                                                        className="form-field"
                                                                    />
                                                                </div>
                                                            </Grid>
                                                        </Grid>
                                                        <ErrorMessage name="coordinates" component="span" className="error-color form-error" />
                                                        <ErrorMessage name="productCountry" component="span" className="error-color form-error" />
                                                        <div className="d-flex flex-column map-container" style={{ marginTop: 10, flexGrow: 1 }}>
                                                            <Map
                                                                ref={mapRef}
                                                                coordinates={{ latitude: values.coordinates?.[0] ?? 44.5215, longitude: values.coordinates?.[1] ?? 11.2833 }}
                                                                onMapClick={(coordinate: ICoordinate) => {
                                                                    if (coordinate.latitude != null && coordinate.longitude != null) {
                                                                        // Validate coordinates before processing
                                                                        if (validateCoordinates(coordinate.latitude, coordinate.longitude)) {
                                                                            setDisableNext(true);
                                                                            setFieldValue('coordinates', [coordinate.latitude, coordinate.longitude]);
                                                                            debounceGetProductCountry(coordinate, setValues);
                                                                        } else {
                                                                            logWarn('ProjectInfo', 'Invalid coordinates from map click:', coordinate, LogTag.VALIDATION);
                                                                            setErrorDialog('coordinates');
                                                                        }
                                                                    }
                                                                }}
                                                                onImageUpdate={handleMapImageUpdate}
                                                            />
                                                        </div>
                                                        {values.productCountry && (
                                                            <Typography variant="caption" mt={1}>
                                                                Detected Country: {values.productCountry}
                                                            </Typography>
                                                        )}
                                                        {disableNext && (
                                                            <Box display="flex" alignItems="center" mt={1}>
                                                                <CircularProgress size={16} sx={{ mr: 1 }} /> <Typography variant="caption">Checking country...</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                                {/* riga 100% per textarea note */}
                                                <Grid item xs={12} md={12}>
                                                    <div className="d-flex flex-column" style={{ marginTop: 'auto' }}>
                                                        <TextArea
                                                            title={t('project_info_project_detail_notes')}
                                                            name="notes"
                                                            rows={4}
                                                            value={values.notes}
                                                            placeholder={t('placeholder') as string}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </Grid>
                                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Button onClick={() => submitForm()} disabled={isSubmitting || disableNext} className="d-flex justify-content-end">
                                                        {submitButtonText}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            <Modal open={openModal} onClose={closeMapModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        {t('map_modal_title', 'Seleziona la posizione del progetto')}
                    </Typography>
                    <Box sx={{ height: '70vh', width: '100%' }}>
                        <Map
                            ref={mapRef} // You might need a different ref or way to interact if this is a separate map instance
                            coordinates={{ latitude: dynamicProject.data?.projectInfo?.coordinates?.[0] ?? 44.5215, longitude: dynamicProject.data?.projectInfo?.coordinates?.[1] ?? 11.2833 }}
                            onMapClick={(coordinate: ICoordinate) => {
                                // This map instance in the modal should probably update a temporary state
                                // or directly call a function to update the main form's values if possible.
                                // For simplicity, this example doesn't fully implement modal map interaction.
                                logDebug('ProjectInfo', 'Map clicked in modal:', coordinate, LogTag.UI_INTERACTIONS);
                                // Ideally, on selecting a point here, you'd update the main form's coordinates
                                // and close the modal.
                            }}
                            onImageUpdate={handleMapImageUpdate} // This might be linked to the main map's image
                        />
                    </Box>
                    <Box mt={1}>
                        <Button onClick={closeMapModal}>{t('close_button', 'Chiudi')}</Button>
                    </Box>
                </Box>
            </Modal>
        </Layout>
    );
};

export default ProjectInfo;
